import { useBoolean } from 'ahooks';
import { Moment } from 'moment';
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { TimesheetAPI } from 'api/timesheets';
import {
  Timesheet,
  TimesheetStatus,
} from 'components/organisms/timesheets/types';
import {
  isUserExcludedFromTimesheet,
  useTimesheetExcludedUsers,
} from 'components/pages/timesheet-settings/excluded-users-settings';
import { useNativeActiveOrInvitedUsersQuery } from 'queries/members';

export const useTimesheetsForApprovers = (startDate: Moment) => {
  const queryClient = useQueryClient();
  const TIMESHEETS_QUERY_KEY = ['timesheets', startDate.format('YYYY-MM-DD')];

  const [_refresh, { toggle: refreshData }] = useBoolean();

  const { data: nativeUsers } = useNativeActiveOrInvitedUsersQuery();
  const excludedUsers = useTimesheetExcludedUsers();
  const nativeUserIds = useMemo(
    () => (nativeUsers ?? []).map(({ userId }) => userId),
    [nativeUsers],
  );

  const { data: userTimesheets = [], isLoading: isTimesheetLoading } = useQuery(
    [TIMESHEETS_QUERY_KEY],
    () => TimesheetAPI.getTimesheets(startDate.format('YYYY-MM-DD')),
  );

  const timesheets = userTimesheets.filter(
    (userTimesheet) =>
      !isUserExcludedFromTimesheet(userTimesheet?.userId)(excludedUsers) &&
      nativeUserIds.includes(userTimesheet?.userId),
  );

  // update query cache with new timesheet data on state change
  const onTimesheetStateChange = useCallback(
    (userId: number, payload: Partial<Omit<Timesheet, 'userId'>>) => {
      queryClient.setQueryData(['TIMESHEETS'], (oldData?: Timesheet[]) => {
        const newTimesheets = oldData?.length
          ? oldData.map((timesheet) => {
              if (timesheet.userId === userId) {
                return {
                  ...timesheet,
                  ...payload,
                };
              }

              return timesheet;
            })
          : [];

        return newTimesheets;
      });
    },
    [queryClient],
  );

  return {
    timesheets,
    isTimesheetLoading,
    pendingApprovalCount: timesheets.filter(
      (timesheet) => timesheet?.status === TimesheetStatus.Submitted,
    )?.length,
    hasApprovalTabPermission: timesheets?.length !== 0,
    refreshData,
    onTimesheetStateChange,
  };
};
